import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { nodeClickDataActions } from "../../state/nodeClickDataState";
import { CustomNodeProps } from "../../utilities/flowBuilder.utility";
import MacroTriggerNode from "./macroTriggerNode";
import MacroMultiActionNode from "./macroMultiActionNode";
import MacroResponseBlockNode from "./macroResponseBlockNode";
import ActionBlockNode from "../flow-builder/actionNode";
import StickyNotesNode from "../flow-builder/stickyNotes";
import { NodeActionActions } from "../../state/nodeActionState";
import { useAppSelector } from "../../state/store";
import CustomNodeOptions from "../flow-builder/customNodeOptions";
import {
  Handle,
  NodeProps,
  Position,
  useReactFlow,
  useUpdateNodeInternals,
} from "reactflow";
import MessageBlockNode from "../flow-builder/messageNode";
import { WarnIcon } from "../../icons/warnIcon";
import { errorDataActions } from "../../state/errorDataState";
import { NodeSubType } from "../../models/nodeSubType";
import {
  FrontendNodeType,
  MacroNodeActionType,
  MacroNodeActionVerb,
} from "./macroNode.types";

/**
 * MacroCustomNode
 *
 * Simplified custom node renderer for macros.
 * Routes to: MacroTriggerNode, ActionBlockNode, StickyNotesNode.
 * No chatbot-specific nodes (Message, Condition, AI, etc.)
 */
const MacroCustomNode = (props: NodeProps) => {
  const { data, isConnectable, id } = props;
  const leftHandlerStyle = {
    top: "36px",
    background: data.nodeData?.connectorColor || "#616161",
    width: "12px",
    height: "12px",
    zIndex: 100,
    left: "-6px",
    boxShadow: "0px 0px 0px 5px #E0E0E0",
    border: "none",
  };
  const rightHandlerStyle = {
    bottom: "15px",
    left: "0 !important",
    right: data.nodeData?.type === "Trigger" ? "" : "-6px",
  };
  const dispatcher = useDispatch();
  const [LocalComponent, setLocalComponent] =
    useState<React.FunctionComponentElement<CustomNodeProps>>();
  const listenToHover = useAppSelector(
    (state) => state.proximityConnectState.listenToHover
  );
  const errorState = useAppSelector((state) => state.errorDataState.errors);
  const errorDataState = useAppSelector((state) => state.errorDataState);
  const currentMacroId = useAppSelector((state) => state.homeState.flowId);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [isWarningExisting, setWarningExitsing] = useState(false);
  const [isErrorExisting, setIsErrorExisting] = useState(false);
  const [nodeOptionsDimension, setNodeOptionsDimension] = useState<{
    width: number;
    height: number;
  }>({ width: 20, height: 20 });
  const { getZoom } = useReactFlow();

  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(data.nodeData.nodeId);
  }, [data.nodeData.childNodes]);

  const editNode = (
    nodeType: string,
    nodeId: string,
    nodeSubType: string,
    nodeIndex: number,
    automationChannel: string
  ) => {
    dispatcher(
      nodeClickDataActions.setEditNode({
        nodeType,
        nodeId,
        nodeSubType,
        nodeIndex,
        automationChannel,
        backendConfig: data?.nodeData?.backendConfig,
      })
    );
  };

  const setNodeId = (nodeId: string | null, subType: string | undefined) => {
    dispatcher(
      NodeActionActions.updateState({
        action: MacroNodeActionVerb.HOVER,
        type: MacroNodeActionType.NODE,
        nodeId: nodeId ? nodeId : null,
        subType: subType ? subType : null,
      })
    );
  };

  useEffect(() => {
    const zoomLevel = getZoom();
    setNodeOptionsDimension({
      width: zoomLevel < 1 ? (2 - zoomLevel) * 20 : 20,
      height: zoomLevel < 1 ? (2 - zoomLevel) * 20 : 20,
    });
  }, [hoveredNodeId]);

  // Check for errors and warnings in the current node
  useEffect(() => {
    if (
      errorState &&
      currentMacroId &&
      errorState[currentMacroId] &&
      errorState[currentMacroId][`${id}`]
    ) {
      const nodeErrorState = errorState[currentMacroId][`${id}`];
      if (nodeErrorState.errors?.length) {
        setIsErrorExisting(true);
      } else {
        setIsErrorExisting(false);
      }
      if (nodeErrorState.warnings?.length) {
        setWarningExitsing(true);
      } else {
        setWarningExitsing(false);
      }
    } else {
      setIsErrorExisting(false);
      setWarningExitsing(false);
    }
  }, [errorState, props?.data?.nodeData, id, currentMacroId]);

  useEffect(() => {
    const propsItem = {
      id,
      nodeData: data.nodeData || data,
      isConnectable,
      leftHandlerStyle,
      rightHandlerStyle,
      getIcon: () => null,
      editNode,
      onHover: setNodeId,
    };

    // Route to macro-specific components
    if (data?.nodeData?.subType === NodeSubType.MACRO_STARTING_POINT) {
      setLocalComponent(<MacroTriggerNode {...propsItem} />);
      return;
    }

    if (data?.nodeData?.subType === NodeSubType.MULTI_ACTION) {
      setLocalComponent(<MacroMultiActionNode {...propsItem} />);
      return;
    }

    if (data?.nodeData?.subType === NodeSubType.RESPONSE_BLOCK) {
      setLocalComponent(<MacroResponseBlockNode {...propsItem} />);
      return;
    }

    switch (data?.nodeData?.type) {
      case FrontendNodeType.ACTION:
        setLocalComponent(<ActionBlockNode {...propsItem} />);
        break;
      case FrontendNodeType.STICKY_NOTES:
        setLocalComponent(<StickyNotesNode {...propsItem} />);
        break;
      case FrontendNodeType.MESSAGE:
        setLocalComponent(<MessageBlockNode {...propsItem} />);
        break;
      default:
        // For any other macro node types that may be added later
        setLocalComponent(<ActionBlockNode {...propsItem} />);
        break;
    }
  }, [props?.data?.nodeData, listenToHover]);

  const showErrorModal = () => {
    dispatcher(
      errorDataActions.updateErrorState({
        ...errorDataState,
        errorModalOpenStatus: true,
        selectedNode: id,
      })
    );
  };

  const nodeType = data?.nodeData?.type;
  const nodeSubType = data?.nodeData?.subType;
  const hasDescription = !!data?.nodeData?.description;
  const showMacroTargetHandle =
    nodeSubType !== NodeSubType.MACRO_STARTING_POINT &&
    nodeType !== FrontendNodeType.STICKY_NOTES &&
    (!hasDescription || nodeSubType === NodeSubType.TICKET_FORM);

  return (
    <div
      onMouseEnter={() => setHoveredNodeId(props.id)}
      onMouseLeave={() =>
        setTimeout(() => {
          setHoveredNodeId(null);
        }, 180)
      }
    >
      {isErrorExisting ? (
        <div
          style={{
            color: "#B92321",
            position: "absolute",
            top: "-30px",
            display: "flex",
            marginLeft: 16,
          }}
        >
          <div onClick={() => showErrorModal()}>
            <WarnIcon width={20} height={20} color={"#B92321"} />
          </div>
          <span style={{ marginLeft: 16 }}>Block defective</span>
        </div>
      ) : isWarningExisting ? (
        <div
          style={{
            color: "#99631B",
            position: "absolute",
            top: "-30px",
            display: "flex",
            marginLeft: 16,
          }}
        >
          <div onClick={() => showErrorModal()}>
            <WarnIcon width={20} height={20} color={"#99631B"} />
          </div>
          <span style={{ marginLeft: 16 }}>Block Has Warnings</span>
        </div>
      ) : null}
      {showMacroTargetHandle && (
        <Handle
          id="target"
          type="target"
          position={Position.Left}
          style={leftHandlerStyle}
          isConnectable={true}
        />
      )}
      {LocalComponent}
      {nodeSubType === NodeSubType.TICKET_FORM && (
        <Handle
          type="source"
          position={Position.Right}
          id="source"
          style={{ ...leftHandlerStyle, ...rightHandlerStyle }}
          isConnectable={true}
        />
      )}
      {hoveredNodeId === props.id && props.id !== "N1" && (
        <CustomNodeOptions
          nodeId={props.id}
          width={nodeOptionsDimension!.width}
          height={nodeOptionsDimension!.height}
          nodeType={props.data?.nodeData?.type}
          nodeIndex={props.data?.nodeData?.nodeIndex?.toString()}
          subType={props.data?.nodeData?.subType}
          isMacro={true}
        />
      )}
    </div>
  );
};

export default memo(MacroCustomNode);
